import {
	IApiQueryParams,
	TVerificationType,
	TFinanceHistoryType,
	ITransferQueryParams,
	IWalletQueryParams,
} from "types/routing"
import { getUrlParams } from "utils/filter"

export const URL_VARS = {
	ROOT: "/",
	ID: "id",
	LOCALE: "locale",
	LOGIN: "login",
	DIIA: "diia",
	INVITE: "invite",
	SUCCESS: "success",
	REGISTER: "register",
	RESET_PASSWORD: "reset-password",
	TRADING_FEES: "trading-fees",
	AML_KYC_POLICY: "aml-kyc-policy",
	RISK_WARNING: "risk-warning",
	PRIVACY_POLICY: "privacy-policy",
	MARGIN_AGREEMENT: "margin-agreement",
	TERMS_OF_USE: "terms-of-use",
	CHARTING: "charting",
	MULTI_CHARTS: "multi-charts",
	FEES: "fees",
	MARGIN_TRADING_FAQ: "margin-trading-faq",
	CHARITY: "charity",
	WELCOME_BONUS: "welcome-bonus",
	BONUS: "bonus",
	AML: "aml",
	LISTING: "listing",
	REQUEST: "request",
	SOCIAL_LISTING: "social-listing",
	HISTORY: "history",
	DONATES_HISTORY: "donates-history",
	SLUG: "slug",
	BUY_CRYPTO: "buy-crypto",
	PAIR: "pair",
	COIN: "coin",
	POPULAR: "popular",
	RECENT: "recent",
	LEADERS: "leaders",
	WORLD_TRADES: "world-trades",
	DETAILS: "details",
	STORIES: "stories",
	CATEGORY: "category",
	NEWS: "news",
	COMPETITIONS: "competitions",
	SUB_ACCOUNT: "sub-account",
	BALANCE: "balance",
	UID: "uid",
	ORDER_MANAGEMENT: "order-management",
	ACCOUNT_MANAGEMENT: "account-management",
	API_MANAGEMENT: "api-management",
	AUTHENTICATOR: "authenticator",
	EDIT: "edit",
	CREATE: "create",
	TRANSFER: "transfer",
	TRANSFER_HISTORY: "transfer-history",
	LOGIN_HISTORY: "login-history",
	PROFILE: "profile",
	DASHBOARD: "dashboard",
	VERIFICATION: "verification",
	IDENTITY: "identity",
	ADDRESS: "address",
	FINANCE: "finance",
	SETTINGS: "settings",
	LANGUAGE_INTERFACE: "language-interface",
	LANGUAGE_CONTENT: "language-content",
	INTERFACE_THEME: "interface-theme",
	TERMINAL_LAYOUT: "terminal-layout",
	LANGUAGE: "language",
	API: "api",
	SECURITY: "security",
	SESSIONS: "sessions",
	NOTIFICATIONS: "notifications",
	FROM_NOTIFICATIONS_PAGE: "fromNotificationsPage",
	WALLETS: "wallets",
	DEPOSITS: "deposits",
	WITHDRAWS: "withdraws",
	WITHDRAW: "withdraw",
	CODE: "code",
	ACTIVE_ORDERS: "active-orders",
	CLOSED_ORDERS: "closed-orders",
	TRADES_HISTORY: "trades-history",
	BORROWS: "borrows",
	REPAYMENTS: "repayments",
	INTERESTS: "interests",
	TRANSFERS: "transfers",
	CREATE_TRANSFER: "create-transfer",
	TXID: "txid",
	APPLY: "apply",
	MARGIN_CALLS: "margin-calls",
	LIQUIDATIONS: "liquidations",
	CREATE_DEPOSIT: "create-deposit",
	CURRENCY: "currency",
	CREATE_WITHDRAW: "create-withdraw",
	VERIFY_WITHDRAW: "verify-withdraw",
	EMAIL: "email",
	CONFIRM: "confirm",
	DEPOSIT_SUCCESS: "deposit-success",
	DEPOSIT_FAILED: "deposit-failed",
	ALPHA_CODE: "alpha-code",
	REFERRALS: "referrals",
	REFERRAL_ACCRUALS: "referral-accruals",
	REFBACK: "refback",
	TRADE: "trade",
	STAKING: "staking",
	PLANS: "plans",
	POSITIONS_ACTIVE: "position/active",
	POSITIONS_HISTORY: "position/history",
	PAYMENT_HISTORY: "payment-history",
	SUPPORT: "support",
	SEARCH: "search",
	TYPE: "type",
	REDIRECT: "redirect",
	VALUE: "value",
	FALSE: "false",
	TRUE: "true",
	OUT: "out",
	IN: "in",
	P2P: "p2p",
	PAYMENT_METHODS: "payment-methods",
	FEEDBACK: "feedback",
	BLOCKED_USERS: "blocked-users",
	MERCHANT: "merchant",
	BECOME_MERCHANT: "become-merchant",
	USER_CENTER: "user-center",
	MAIN: "main",
	ORDERS: "orders",
	ADS: "ads",
	EXPRESS: "express",
	CREATE_ORDER: "create-order",
	FUNDING_WALLET: "funding-wallet",
	ABOUT_US: "about-us",
	DEAD_END_ROUTE: "*",
	OVERVIEW: "overview",
} as const

export const routes = {
	root: `${URL_VARS.ROOT}`,
	invite: `/${URL_VARS.INVITE}`,
	resetPassword: `/${URL_VARS.RESET_PASSWORD}`,
	tradingFees: `/${URL_VARS.TRADING_FEES}`,
	amlKycPolicy: `/${URL_VARS.AML_KYC_POLICY}`,
	riskWarning: `/${URL_VARS.RISK_WARNING}`,
	privacyPolicy: `/${URL_VARS.PRIVACY_POLICY}`,
	marginAgreement: `/${URL_VARS.MARGIN_AGREEMENT}`,
	termsOfUse: `/${URL_VARS.TERMS_OF_USE}`,
	charting: `/${URL_VARS.CHARTING}`,
	multiCharts: `/${URL_VARS.MULTI_CHARTS}`,
	fees: `/${URL_VARS.FEES}`,
	marginTradingFaq: `/${URL_VARS.MARGIN_TRADING_FAQ}`,
	charity: `/${URL_VARS.CHARITY}`,
	aml: `/${URL_VARS.AML}`,
	login: {
		root: `/${URL_VARS.LOGIN}`,
		redirect: (redirectUrl: string) => `${routes.login.root}?${URL_VARS.REDIRECT}=${redirectUrl}`,
	},
	register: {
		root: `/${URL_VARS.REGISTER}`,
		getRegisterWithEmail: (email?: string) =>
			`${routes.register.root}${email && `/?${URL_VARS.EMAIL}=${email}`}`,
	},
	trade: {
		root: `/${URL_VARS.TRADE}`,
		pair: `/${URL_VARS.TRADE}/:${URL_VARS.PAIR}`,
		getPair: (pair: string) => `${routes.trade.root}/${pair}`,
	},
	listing: {
		root: `/${URL_VARS.LISTING}`,
		request: `/${URL_VARS.LISTING}/${URL_VARS.REQUEST}`,
	},
	socialListing: {
		root: `/${URL_VARS.SOCIAL_LISTING}`,
		history: `/${URL_VARS.SOCIAL_LISTING}/${URL_VARS.HISTORY}`,
		project: `/${URL_VARS.SOCIAL_LISTING}/:${URL_VARS.SLUG}`,
		projectDonatesHistory: `/${URL_VARS.SOCIAL_LISTING}/:${URL_VARS.SLUG}/${URL_VARS.DONATES_HISTORY}`,
		getProjectUrl: (slug: string) => `${routes.socialListing.root}/${slug}`,
		getProjectDonatesHistoryUrl: (slug: string) =>
			`${routes.socialListing.root}/${slug}/${URL_VARS.DONATES_HISTORY}`,
	},
	welcomeBonus: {
		root: `/${URL_VARS.WELCOME_BONUS}`,
		bonus: `/${URL_VARS.WELCOME_BONUS}/${URL_VARS.BONUS}`,
	},
	buyCrypto: {
		root: `/${URL_VARS.BUY_CRYPTO}`,
		pair: `/${URL_VARS.BUY_CRYPTO}/:${URL_VARS.PAIR}`,
		getPair: (pair: string) => `${routes.buyCrypto.root}/${pair}`,
	},
	stories: {
		root: `/${URL_VARS.STORIES}`,
		category: `/${URL_VARS.STORIES}/${URL_VARS.CATEGORY}/:${URL_VARS.SLUG}`,
		story: `/${URL_VARS.STORIES}/:${URL_VARS.SLUG}`,
		getCategory: (slug: string) => `/${URL_VARS.STORIES}/${URL_VARS.CATEGORY}/${slug}`,
		getStory: (slug: string) => `/${URL_VARS.STORIES}/${slug}`,
	},
	news: {
		root: `/${URL_VARS.NEWS}`,
		news: `/${URL_VARS.NEWS}/:${URL_VARS.SLUG}`,
		getNews: (slug: string) => `${routes.news.root}/${slug}`,
	},
	competitions: {
		root: `/${URL_VARS.COMPETITIONS}`,
		details: `/${URL_VARS.COMPETITIONS}/:${URL_VARS.SLUG}`,
		bonus: `/${URL_VARS.COMPETITIONS}/:${URL_VARS.SLUG}/${URL_VARS.BONUS}`,
		getCompetition: (slug: string) => `${routes.competitions.root}/${slug}`,
		getCompetitionBonus: (slug: string) => `${routes.competitions.root}/${slug}/${URL_VARS.BONUS}`,
	},
	coin: {
		root: `/${URL_VARS.COIN}`,
		details: `/${URL_VARS.COIN}/${URL_VARS.DETAILS}/:${URL_VARS.COIN}`,
		popular: `/${URL_VARS.COIN}/${URL_VARS.POPULAR}`,
		recent: `/${URL_VARS.COIN}/${URL_VARS.RECENT}`,
		leaders: `/${URL_VARS.COIN}/${URL_VARS.LEADERS}`,
		worldTrades: `/${URL_VARS.COIN}/${URL_VARS.WORLD_TRADES}`,
		getCoin: (coin: string) => `${routes.coin.root}/${URL_VARS.DETAILS}/${coin}`,
	},
	staking: {
		root: `/${URL_VARS.STAKING}`,
		plans: `/${URL_VARS.STAKING}/${URL_VARS.PLANS}`,
		positionsActive: `/${URL_VARS.STAKING}/${URL_VARS.POSITIONS_ACTIVE}`,
		positionsHistory: `/${URL_VARS.STAKING}/${URL_VARS.POSITIONS_HISTORY}`,
		paymentHistory: `/${URL_VARS.STAKING}/${URL_VARS.PAYMENT_HISTORY}`,
	},
	support: {
		root: `/${URL_VARS.SUPPORT}`,
		request: `/${URL_VARS.SUPPORT}/${URL_VARS.REQUEST}`,
		search: `/${URL_VARS.SUPPORT}/${URL_VARS.SEARCH}`,
		news: `/${URL_VARS.SUPPORT}/${URL_VARS.NEWS}`,
		article: `/${URL_VARS.SUPPORT}/:${URL_VARS.SLUG}`,
		getSearchValue: (value: string) => `${routes.support.search}?${URL_VARS.VALUE}=${value}`,
		getArticle: (slug: string) => `/${URL_VARS.SUPPORT}/${slug}`,
	},
	aboutUs: {
		root: `/${URL_VARS.ABOUT_US}`,
	},
	profile: {
		root: `/${URL_VARS.PROFILE}`,
		dashboard: `/${URL_VARS.PROFILE}/${URL_VARS.DASHBOARD}`,
		notification: `/${URL_VARS.PROFILE}/${URL_VARS.NOTIFICATIONS}`,
		notificationDetails: `/${URL_VARS.PROFILE}/${URL_VARS.NOTIFICATIONS}/:${URL_VARS.SLUG}`,
		wallets: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}`,
		walletOverview: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.OVERVIEW}`,
		fundingWallet: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.FUNDING_WALLET}`,
		walletDetails: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.DETAILS}/:${URL_VARS.CODE}`,
		createDeposit: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.CREATE_DEPOSIT}`,
		createDepositCurrency: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.CREATE_DEPOSIT}/:${URL_VARS.CURRENCY}`,
		createWithdraw: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.CREATE_WITHDRAW}`,
		createWithdrawCurrency: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.CREATE_WITHDRAW}/:${URL_VARS.CURRENCY}`,
		verifyWithdraw: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.VERIFY_WITHDRAW}`,
		createTransfer: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.CREATE_TRANSFER}`,
		createTransferCurrency: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.CREATE_TRANSFER}/:${URL_VARS.CURRENCY}`,
		getDepositCurrency: (currency: string) => `${routes.profile.createDeposit}/${currency}`,
		getWithdrawCurrency: (currency: string) => `${routes.profile.createWithdraw}/${currency}`,
		getWalletsDetails: (code: string, params: IWalletQueryParams) =>
			`${routes.profile.wallets}/${URL_VARS.DETAILS}/${code}${getUrlParams(params)}`,
		getTransferCurrency: (currency: string) => `${routes.profile.createTransfer}/${currency}`,
		getNotificationDetails: (slug: string, isFromNotificationsPage: boolean) =>
			`${routes.profile.notification}/${slug}/?${URL_VARS.FROM_NOTIFICATIONS_PAGE}=${
				isFromNotificationsPage ? URL_VARS.TRUE : URL_VARS.FALSE
			}`,
	},
	dashboard: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.DASHBOARD}`,
	},
	settings: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.SETTINGS}`,
		languageInterface: `/${URL_VARS.PROFILE}/${URL_VARS.SETTINGS}/${URL_VARS.LANGUAGE_INTERFACE}`,
		languageContent: `/${URL_VARS.PROFILE}/${URL_VARS.SETTINGS}/${URL_VARS.LANGUAGE_CONTENT}`,
		interfaceTheme: `/${URL_VARS.PROFILE}/${URL_VARS.SETTINGS}/${URL_VARS.INTERFACE_THEME}`,
		terminalLayout: `/${URL_VARS.PROFILE}/${URL_VARS.SETTINGS}/${URL_VARS.TERMINAL_LAYOUT}`,
		notifications: `/${URL_VARS.PROFILE}/${URL_VARS.SETTINGS}/${URL_VARS.NOTIFICATIONS}`,
		languageNotifications: `/${URL_VARS.PROFILE}/${URL_VARS.SETTINGS}/${URL_VARS.NOTIFICATIONS}/${URL_VARS.LANGUAGE}`,
	},
	api: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.API}`,
		details: `/${URL_VARS.PROFILE}/${URL_VARS.API}/:${URL_VARS.SLUG}`,
		getDetails: (slug: string) => `${routes.api.root}/${slug}`,
	},
	security: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.SECURITY}`,
		sessions: `/${URL_VARS.PROFILE}/${URL_VARS.SECURITY}/${URL_VARS.SESSIONS}`,
		authenticator: `/${URL_VARS.PROFILE}/${URL_VARS.SECURITY}/${URL_VARS.AUTHENTICATOR}`,
	},
	financeHistory: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.HISTORY}`,
		withdraws: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.HISTORY}/${URL_VARS.WITHDRAWS}`,
		transfers: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.HISTORY}/${URL_VARS.TRANSFERS}`,
		getType: (type: TFinanceHistoryType) => `${routes.financeHistory.root}/${type}`,
	},
	transfers: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.TRANSFERS}`,
		accept: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.TRANSFERS}/:${URL_VARS.TXID}/${URL_VARS.APPLY}`,
		history: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.TRANSFERS}/${URL_VARS.HISTORY}`,
		getAcceptTransfer: (slug: string) => `${routes.transfers.root}/${slug}/${URL_VARS.APPLY}`,
	},
	alphaCodes: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.ALPHA_CODE}`,
		history: `/${URL_VARS.PROFILE}/${URL_VARS.WALLETS}/${URL_VARS.ALPHA_CODE}/${URL_VARS.HISTORY}`,
	},
	referrals: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.REFERRALS}`,
		referralAccruals: `/${URL_VARS.PROFILE}/${URL_VARS.REFERRALS}/${URL_VARS.REFERRAL_ACCRUALS}`,
		refback: `/${URL_VARS.PROFILE}/${URL_VARS.REFERRALS}/${URL_VARS.REFBACK}`,
	},
	verification: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.VERIFICATION}`,
		overview: `/${URL_VARS.PROFILE}/${URL_VARS.VERIFICATION}/${URL_VARS.OVERVIEW}`,
		identity: `/${URL_VARS.PROFILE}/${URL_VARS.VERIFICATION}/${URL_VARS.IDENTITY}`,
		address: `/${URL_VARS.PROFILE}/${URL_VARS.VERIFICATION}/${URL_VARS.ADDRESS}`,
		finance: `/${URL_VARS.PROFILE}/${URL_VARS.VERIFICATION}/${URL_VARS.FINANCE}`,
		identityDiia: `/${URL_VARS.PROFILE}/${URL_VARS.VERIFICATION}/${URL_VARS.IDENTITY}/${URL_VARS.DIIA}`,
		identityDiiaSuccess: `/${URL_VARS.VERIFICATION}/${URL_VARS.IDENTITY}/${URL_VARS.DIIA}/${URL_VARS.SUCCESS}`,
		getVerificationType: (type: TVerificationType) => `${routes.verification.root}/${type}`,
	},
	history: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}`,
		activeOrders: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}/${URL_VARS.ACTIVE_ORDERS}`,
		closedOrders: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}/${URL_VARS.CLOSED_ORDERS}`,
		tradesHistory: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}/${URL_VARS.TRADES_HISTORY}`,
		borrows: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}/${URL_VARS.BORROWS}`,
		repayments: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}/${URL_VARS.REPAYMENTS}`,
		interests: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}/${URL_VARS.INTERESTS}`,
		marginTransfers: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}/${URL_VARS.TRANSFERS}`,
		marginCalls: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}/${URL_VARS.MARGIN_CALLS}`,
		liquidations: `/${URL_VARS.PROFILE}/${URL_VARS.HISTORY}/${URL_VARS.LIQUIDATIONS}`,
	},
	subAccounts: {
		root: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}`,
		balances: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.BALANCE}`,
		balanceDetails: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.BALANCE}/:${URL_VARS.UID}`,
		orderManagement: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.ORDER_MANAGEMENT}`,
		accountManagement: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.ACCOUNT_MANAGEMENT}`,
		authenticator: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.ACCOUNT_MANAGEMENT}/${URL_VARS.AUTHENTICATOR}`,
		apiManagement: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.API_MANAGEMENT}`,
		apiDetails: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.API_MANAGEMENT}/:${URL_VARS.UID}`,
		apiEdit: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.API_MANAGEMENT}/:${URL_VARS.UID}/${URL_VARS.EDIT}`,
		apiCreate: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.API_MANAGEMENT}/${URL_VARS.CREATE}`,
		transfer: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.TRANSFER}`,
		transferHistory: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.TRANSFER_HISTORY}`,
		loginHistory: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.LOGIN_HISTORY}`,
		create: `/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.CREATE}`,
		getBalanceDetails: (uid: string) =>
			`/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.BALANCE}/${uid}`,
		getApiDetails: (uid: string) =>
			`/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.API_MANAGEMENT}/${uid}`,
		getApiEdit: (uid: string) =>
			`/${URL_VARS.PROFILE}/${URL_VARS.SUB_ACCOUNT}/${URL_VARS.API_MANAGEMENT}/${uid}/${URL_VARS.EDIT}`,
		getApiCreateQuery: (params: IApiQueryParams) =>
			`${routes.subAccounts.apiCreate}${getUrlParams(params)}`,
		getTransferQuery: (params: ITransferQueryParams) =>
			`${routes.subAccounts.transfer}${getUrlParams(params)}`,
	},
	p2p: {
		root: `/${URL_VARS.P2P}`,
		main: `/${URL_VARS.P2P}/${URL_VARS.MAIN}`,
		orders: `/${URL_VARS.P2P}/${URL_VARS.ORDERS}`,
		orderDetails: `/${URL_VARS.P2P}/${URL_VARS.ORDERS}/:${URL_VARS.ID}`,
		createOrder: `/${URL_VARS.P2P}/${URL_VARS.CREATE_ORDER}`,
		ads: `/${URL_VARS.P2P}/${URL_VARS.ADS}`,
		express: `/${URL_VARS.P2P}/${URL_VARS.EXPRESS}`,
		merchant: `/${URL_VARS.P2P}/${URL_VARS.MERCHANT}`,
		becomeMerchant: `/${URL_VARS.P2P}/${URL_VARS.BECOME_MERCHANT}`,
		userCenter: `/${URL_VARS.P2P}/${URL_VARS.USER_CENTER}`,
		userDetails: `/${URL_VARS.P2P}/${URL_VARS.USER_CENTER}/:${URL_VARS.ID}`,
		getOrderDetails: (uid: number | string) => `/${URL_VARS.P2P}/${URL_VARS.ORDERS}/${uid}`,
		getUserDetails: (uid: number | string) => `/${URL_VARS.P2P}/${URL_VARS.USER_CENTER}/${uid}`,
	},
}
